import type { CompanyLocationDto, DeliveryLocationDto } from '@api/customers'

import {
  MolsonBox,
  MolsonButton,
  MolsonIcon,
  MolsonTooltip,
  MolsonTypography
} from '@components/reusable'
import { Radio } from '@mui/material'
import { molsonUseTheme } from '@theme/customMuiExports'
import { useTranslation } from 'react-i18next'
import { CustomChip } from '../custom-chip/CustomChip'
import { OriginalInfoChip } from '../original-info-chip/OriginalInfoChip'

type AddressType = 'billing' | 'delivery'

type LocationDto = CompanyLocationDto | DeliveryLocationDto

interface AddressLocationItemProps<T extends LocationDto> {
  /** The address location to display */
  location: T
  /** The type of address (billing or delivery) */
  addressType: AddressType
  /** Callback when address is selected */
  onSelectAddressId: (id?: number) => void
  /** The currently selected address ID */
  selectedAddressId?: null | number
  /** The default address ID for this type */
  defaultAddressId?: number | null
  /** Callback when delete dialog should open */
  onOpenDeleteDialog: (location: T) => void
  /** Callback when edit dialog should open */
  onOpenEditDialog: (location: T) => void
  /** Whether this is the last address of this type */
  isLastAddress: boolean
  /** Whether this is the original location from ERP */
  isOriginalLocation: boolean
  /** Translation key prefix for this context */
  translationKeyPrefix: string
}

const keysToRender = [
  'addressName',
  'address1',
  'address2',
  'address3',
  'state',
  'city',
  'zipCode',
  'country'
] as const

export const AddressLocationItem = <T extends LocationDto>({
  location,
  addressType,
  onSelectAddressId,
  selectedAddressId,
  defaultAddressId,
  onOpenDeleteDialog,
  onOpenEditDialog,
  isLastAddress,
  isOriginalLocation,
  translationKeyPrefix
}: AddressLocationItemProps<T>) => {
  const theme = molsonUseTheme()

  const { t } = useTranslation('')
  const commonTranslationKey = 'common'
  const nonCommonTranslationkey = translationKeyPrefix

  const checked = selectedAddressId === location.id

  const disableDelete = isOriginalLocation || isLastAddress

  const editTooltip = () => {
    if (isOriginalLocation && addressType === 'billing') {
      return t(`${nonCommonTranslationkey}.cannotEditErpBillingLocationTooltip`)
    }
    if (isOriginalLocation && addressType === 'delivery') {
      return t(
        `${nonCommonTranslationkey}.cannotEditErpDeliveryLocationTooltip`
      )
    }
    return ''
  }

  const deleteTooltip = () => {
    if (isOriginalLocation && addressType === 'billing') {
      return t(
        `${nonCommonTranslationkey}.cannotDeleteErpBillingLocationTooltip`
      )
    }
    if (isOriginalLocation && addressType === 'delivery') {
      return t(
        `${nonCommonTranslationkey}.cannotDeleteErpDeliveryLocationTooltip`
      )
    }
    if (isLastAddress && addressType === 'billing') {
      return t(
        `${nonCommonTranslationkey}.cannotDeleteLastBillingLocationTooltip`
      )
    }
    if (isLastAddress && addressType === 'delivery') {
      return t(
        `${nonCommonTranslationkey}.cannotDeleteLastDeliveryLocationTooltip`
      )
    }
    return ''
  }

  const defaultAddressLabel =
    addressType === 'billing'
      ? `${commonTranslationKey}.defaultBillingAddress`
      : `${commonTranslationKey}.defaultDeliveryAddress`

  return (
    <MolsonBox
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      width="100%"
    >
      <MolsonBox display="flex" justifyContent="space-between">
        <MolsonBox display="flex" alignItems="flex-start" gap={1}>
          <Radio
            checked={checked}
            onChange={() => onSelectAddressId?.(location.id)}
            sx={{
              '&.Mui-checked': {
                color: checked
                  ? theme.palette.primary.light
                  : theme.palette.secondary[900]
              },
              mt: -0.5
            }}
          />
          <MolsonBox>
            <MolsonTypography variant="h6">
              {location[keysToRender[0]]}
            </MolsonTypography>
            {keysToRender.slice(1).map(key => (
              <MolsonTypography key={key}>
                {`${location[key]}`}
              </MolsonTypography>
            ))}
          </MolsonBox>
        </MolsonBox>
        <MolsonBox
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
        >
          <MolsonBox display="flex" gap={1} justifyContent="flex-end">
            <MolsonTooltip title={editTooltip()}>
              <MolsonButton
                color="secondary"
                hasIconOnly={true}
                onClick={() => onOpenEditDialog(location)}
                variant="outlined"
                disabled={isOriginalLocation}
              >
                <MolsonIcon variant="EDIT_PENCIL" />
              </MolsonButton>
            </MolsonTooltip>

            <MolsonTooltip title={deleteTooltip()}>
              <MolsonButton
                color="error"
                hasIconOnly={true}
                onClick={() => onOpenDeleteDialog(location)}
                variant="outlined"
                disabled={disableDelete}
              >
                <MolsonIcon variant="TRASH_CONTAINED" iconColor="error" />
              </MolsonButton>
            </MolsonTooltip>
          </MolsonBox>
        </MolsonBox>
      </MolsonBox>

      <MolsonBox display="flex" gap={1} mt={1.5} alignItems="flex-end">
        {defaultAddressId === location.id && (
          <CustomChip text={t(defaultAddressLabel)} />
        )}
        {isOriginalLocation && (
          <OriginalInfoChip
            label={t(`${nonCommonTranslationkey}.originalLocationChipLabel`)}
          />
        )}
      </MolsonBox>
    </MolsonBox>
  )
}
